import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Form, InputNumber, Button, message, Tag, Spin, Typography, Space } from "antd";
import { DownloadOutlined, EyeOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Booking } from "../../../types/booking";
import { supabase } from "../../../lib/supabase";
import {
  LeanCustomsService,
  type LeanCustomsDeclarationDocument,
} from "../../../services/leancustomsService";
import { extractDutyBreakdown } from "../../../utils/leanCustomsUtbTtwAmounts";
import {
  isUtbDeclarationDocument,
} from "../../../utils/leanCustomsDeclarationDocumentsSort";
import { formatByteSize } from "../../../utils/leanCustomsRecordUtils";
import { parseUtbPdf } from "../../../utils/utbPdfParser";
import InvoiceCustomsDeclarationLink from "./InvoiceCustomsDeclarationLink";

const { Text } = Typography;

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at: string;
}

type CustomsLineInput = {
  description: string;
  quantity: number;
  unit_price: number;
  vat_percentage: number;
  cost_type: string;
};

interface CustomsSectionProps {
  selectedBooking: Booking | null;
  onAddCustomsLines: (lines: CustomsLineInput[]) => void;
  existingCostTypes: string[];
  /** Called after a LeanCustoms declaration is saved on the booking. */
  onDeclarationLinked?: (declarationId: string) => void;
}

function resolveCompanyId(booking: Booking): number | undefined {
  if (booking.company_id) return booking.company_id;
  const info = booking.company_info;
  if (!info) return undefined;
  const company = Array.isArray(info) ? info[0] : info;
  return company?.id ?? undefined;
}

/** Resolve company payment term (joined object or id) from a booking. */
function resolvePricingPaymentTerm(
  booking: Booking
): { term: PricingPaymentTerm | null; termId: number } {
  const company = booking.company_info
    ? Array.isArray(booking.company_info)
      ? booking.company_info[0]
      : booking.company_info
    : null;

  const raw = (company as { pricing_payment_term?: unknown } | null)
    ?.pricing_payment_term;

  if (raw && typeof raw === "object") {
    const obj = (Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown> | undefined;
    if (obj) {
      const interest = Number(obj.interest_vat_import_duties);
      const id = Number(obj.id);
      if (Number.isFinite(interest) && Number.isFinite(id)) {
        return {
          term: {
            id,
            days: Number(obj.days) || 0,
            interest_vat_import_duties: interest,
            created_at: String(obj.created_at ?? ""),
          },
          termId: id,
        };
      }
      if (Number.isFinite(id) && id > 0) return { term: null, termId: id };
    }
  }

  if (typeof raw === "number" && raw > 0) return { term: null, termId: raw };

  return { term: null, termId: 5 };
}

const CustomsSection: React.FC<CustomsSectionProps> = ({
  selectedBooking,
  onAddCustomsLines,
  existingCostTypes,
  onDeclarationLinked,
}) => {
  const [paymentTerm, setPaymentTerm] = useState<PricingPaymentTerm | null>(null);
  const [btwValues, setBtwValues] = useState({ quantity: 1, unitPrice: 0, vatPercentage: 0 });
  const [invoerrechtenValues, setInvoerrechtenValues] = useState({ quantity: 1, unitPrice: 0, vatPercentage: 0 });
  const [leanCustomsSource, setLeanCustomsSource] = useState<string | false>(false);
  const [loadingLeanCustoms, setLoadingLeanCustoms] = useState(false);
  const [leanCustomsError, setLeanCustomsError] = useState<string | null>(null);

  const [utbDoc, setUtbDoc] = useState<LeanCustomsDeclarationDocument | null>(null);
  const [utbPreviewUrl, setUtbPreviewUrl] = useState<string | null>(null);
  const [downloadingUtb, setDownloadingUtb] = useState(false);
  const [loadingUtbPreview, setLoadingUtbPreview] = useState(false);

  // Cleanup blob URL on unmount or change
  useEffect(() => {
    return () => {
      if (utbPreviewUrl) URL.revokeObjectURL(utbPreviewUrl);
    };
  }, [utbPreviewUrl]);

  useEffect(() => {
    const fetchCustomsData = async () => {
      if (!selectedBooking?.leancustoms_declaration_id) {
        setLeanCustomsSource(false);
        setUtbDoc(null);
        setUtbPreviewUrl(null);
        setLeanCustomsError(
          selectedBooking ? "No LeanCustoms declaration linked to this booking" : null
        );
        return;
      }

      setLoadingLeanCustoms(true);
      setLeanCustomsError(null);
      const companyId = resolveCompanyId(selectedBooking);
      const declId = selectedBooking.leancustoms_declaration_id;

      try {
        const [itemsRes, docsRes] = await Promise.all([
          LeanCustomsService.listDeclarationItems(declId, companyId),
          LeanCustomsService.listDeclarationDocuments(declId, companyId),
        ]);

        // --- Documents: find UTB ---
        let utb: LeanCustomsDeclarationDocument | null = null;
        if (docsRes.success && docsRes.data?.results) {
          utb = docsRes.data.results.find(isUtbDeclarationDocument) ?? null;
          setUtbDoc(utb);
        } else {
          setUtbDoc(null);
        }

        // --- Try extracting duty amounts from multiple sources ---
        let foundAmounts = false;

        // Source 1: declaration items duty_tax_fees (payment / Verschuldigd only)
        if (itemsRes.success && itemsRes.data?.results && itemsRes.data.results.length > 0) {
          const breakdown = extractDutyBreakdown(itemsRes.data.results);
          if (breakdown.btwBijInvoer > 0 || breakdown.invoerrechten > 0) {
            setBtwValues((prev) => ({ ...prev, unitPrice: breakdown.btwBijInvoer }));
            setInvoerrechtenValues((prev) => ({ ...prev, unitPrice: breakdown.invoerrechten }));
            setLeanCustomsSource("LeanCustoms API");
            foundAmounts = true;
          }
        }

        // Source 2: parse the UTB PDF — use Verschuldigd (payment) column only
        if (!foundAmounts && utb) {
          let blob: Blob | null = null;
          try {
            blob = await LeanCustomsService.downloadDeclarationDocument(
              declId, utb.id, companyId
            );
          } catch (dlErr) {
            console.warn("Could not download UTB PDF:", dlErr);
          }

          if (blob) {
            // Always set preview URL regardless of parsing success
            if (utbPreviewUrl) URL.revokeObjectURL(utbPreviewUrl);
            setUtbPreviewUrl(URL.createObjectURL(blob));

            try {
              const parsed = await parseUtbPdf(blob);
              if (parsed.rawText) {
                console.log("UTB PDF text (no duty lines matched):", parsed.rawText);
              }
              // Matched duty lines are authoritative even when Verschuldigd is €0
              // (e.g. VAT held as Zekerheid / surety only — not invoiceable).
              if (parsed.lines.length > 0) {
                setBtwValues((prev) => ({ ...prev, unitPrice: parsed.btwBijInvoer }));
                setInvoerrechtenValues((prev) => ({ ...prev, unitPrice: parsed.invoerrechten }));
                setLeanCustomsSource("Extracted from UTB PDF");
                foundAmounts = true;
              }
            } catch (pdfErr) {
              console.warn("Could not parse UTB PDF:", pdfErr);
            }
          }
        }

        if (!foundAmounts) {
          setLeanCustomsSource(false);
          setLeanCustomsError("Enter amounts manually from UTB document below");
        }
      } catch (err) {
        console.error("Failed to fetch customs data from LeanCustoms:", err);
        setLeanCustomsError(err instanceof Error ? err.message : "Unexpected error");
        setLeanCustomsSource(false);
      } finally {
        setLoadingLeanCustoms(false);
      }
    };

    fetchCustomsData();
  }, [selectedBooking]);

  // Fetch payment terms when booking changes
  useEffect(() => {
    const fetchPaymentTerms = async () => {
      if (!selectedBooking) { setPaymentTerm(null); return; }
      try {
        const { term, termId } = resolvePricingPaymentTerm(selectedBooking);
        if (term) {
          setPaymentTerm(term);
          return;
        }

        const { data: pricingTerm, error } = await supabase
          .from("pricing_payment_terms").select("*").eq("id", termId).single();
        if (!error && pricingTerm) {
          setPaymentTerm(pricingTerm);
        } else {
          // No configured term: default voorschotprovisie is 4%
          setPaymentTerm({ id: 5, days: 1, interest_vat_import_duties: 4, created_at: "" });
        }
      } catch (error) {
        console.error("Error in fetchPaymentTerms:", error);
      }
    };
    fetchPaymentTerms();
  }, [selectedBooking]);

  const handleUtbPreview = useCallback(async () => {
    if (!utbDoc || !selectedBooking?.leancustoms_declaration_id) return;
    setLoadingUtbPreview(true);
    try {
      const blob = await LeanCustomsService.downloadDeclarationDocument(
        selectedBooking.leancustoms_declaration_id,
        utbDoc.id,
        resolveCompanyId(selectedBooking)
      );
      if (utbPreviewUrl) URL.revokeObjectURL(utbPreviewUrl);
      setUtbPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      message.error("Failed to load UTB preview");
      console.error(err);
    } finally {
      setLoadingUtbPreview(false);
    }
  }, [utbDoc, selectedBooking, utbPreviewUrl]);

  const handleUtbDownload = useCallback(async () => {
    if (!utbDoc || !selectedBooking?.leancustoms_declaration_id) return;
    setDownloadingUtb(true);
    try {
      const blob = await LeanCustomsService.downloadDeclarationDocument(
        selectedBooking.leancustoms_declaration_id,
        utbDoc.id,
        resolveCompanyId(selectedBooking)
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = utbDoc.filename || "UTB.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Failed to download UTB");
      console.error(err);
    } finally {
      setDownloadingUtb(false);
    }
  }, [utbDoc, selectedBooking]);

  // Use company payment-term interest when set (>0); otherwise default to 4%
  const configuredInterest = Number(paymentTerm?.interest_vat_import_duties);
  const interestRate =
    Number.isFinite(configuredInterest) && configuredInterest > 0
      ? configuredInterest
      : 4;

  const hasBtw = existingCostTypes.includes("customs_btw");
  const hasDuties = existingCostTypes.includes("customs_duties");

  const btwAmount = btwValues.quantity * btwValues.unitPrice;
  const dutiesAmount = invoerrechtenValues.quantity * invoerrechtenValues.unitPrice;

  /** One 4% line over BTW + invoerrechten combined (not two separate provisie lines). */
  const buildCombinedCommissionLine = (): CustomsLineInput | null => {
    const base = btwAmount + dutiesAmount;
    if (interestRate <= 0 || base <= 0) return null;
    return {
      description: `Voorschotprovisie BTW & Invoerrechten (${interestRate}%)`,
      quantity: 1,
      unit_price: Math.round(base * (interestRate / 100) * 100) / 100,
      vat_percentage: 0,
      cost_type: "customs_commission",
    };
  };

  const buildBtwLines = (): CustomsLineInput[] => [{
    description: "BTW bij Invoer",
    quantity: btwValues.quantity,
    unit_price: Math.round(btwValues.unitPrice * 100) / 100,
    vat_percentage: 0,
    cost_type: "customs_btw",
  }];

  const buildDutiesLines = (): CustomsLineInput[] => [{
    description: "Invoerrechten",
    quantity: invoerrechtenValues.quantity,
    unit_price: Math.round(invoerrechtenValues.unitPrice * 100) / 100,
    vat_percentage: 0,
    cost_type: "customs_duties",
  }];

  const withCombinedCommission = (lines: CustomsLineInput[]): CustomsLineInput[] => {
    const commission = buildCombinedCommissionLine();
    return commission ? [...lines, commission] : lines;
  };

  const addBtwToInvoiceLines = () => {
    if (btwValues.unitPrice === 0) {
      message.error("Please enter a unit price for BTW bij Invoer");
      return;
    }
    const lines = withCombinedCommission(buildBtwLines());
    onAddCustomsLines(lines);
    message.success(`BTW bij Invoer ${hasBtw ? "updated" : "added"}`);
  };

  const addInvoerrechtenToInvoiceLines = () => {
    const lines = withCombinedCommission(buildDutiesLines());
    onAddCustomsLines(lines);
    message.success(`Invoerrechten ${hasDuties ? "updated" : "added"}`);
  };

  const addAllToInvoiceLines = () => {
    const lines: CustomsLineInput[] = [];
    if (btwValues.unitPrice > 0) lines.push(...buildBtwLines());
    if (invoerrechtenValues.unitPrice > 0 || invoerrechtenValues.quantity > 0) {
      lines.push(...buildDutiesLines());
    }
    onAddCustomsLines(withCombinedCommission(lines));
    message.success(`Customs lines ${hasBtw || hasDuties ? "updated" : "added"}`);
  };

  if (!selectedBooking) return null;

  return (
    <Card
      size="small"
      style={{ marginBottom: 16, border: "2px solid #52c41a", backgroundColor: "#f6ffed" }}
      title={
        <span style={{ color: "#52c41a", fontWeight: "bold", fontSize: "14px" }}>
          🛃 Customs
          {loadingLeanCustoms && <Spin size="small" style={{ marginLeft: 8 }} />}
          {leanCustomsSource && (
            <Tag color="blue" style={{ marginLeft: 8, fontSize: "10px" }}>{leanCustomsSource}</Tag>
          )}
          {!loadingLeanCustoms && !leanCustomsSource && leanCustomsError && (
            <Tag color="orange" style={{ marginLeft: 8, fontSize: "10px" }}>{leanCustomsError}</Tag>
          )}
        </span>
      }
      bodyStyle={{ padding: "12px", backgroundColor: "#f6ffed" }}
    >
      {selectedBooking && !selectedBooking.leancustoms_declaration_id && onDeclarationLinked && (
        <InvoiceCustomsDeclarationLink
          booking={selectedBooking}
          onLinked={onDeclarationLinked}
        />
      )}

      {/* UTB Document */}
      {utbDoc && (
        <div style={{ marginBottom: 12, padding: "8px 10px", background: "#fff", borderRadius: 6, border: "1px solid #d9d9d9" }}>
          <Row align="middle" justify="space-between">
            <Space size={8}>
              <FilePdfOutlined style={{ color: "#cf1322", fontSize: 16 }} />
              <div>
                <Text strong style={{ fontSize: 12 }}>{utbDoc.filename}</Text>
                <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                  {formatByteSize(utbDoc.byte_size ?? 0)}
                </Text>
              </div>
              <Tag color="blue" style={{ fontSize: 10 }}>UTB</Tag>
            </Space>
            <Space size={4}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={loadingUtbPreview}
                onClick={handleUtbPreview}
              >
                {utbPreviewUrl ? "Reload" : "Preview"}
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingUtb}
                onClick={handleUtbDownload}
              >
                Download
              </Button>
            </Space>
          </Row>
          {utbPreviewUrl && (
            <iframe
              src={utbPreviewUrl}
              title="UTB Preview"
              style={{ width: "100%", height: 400, border: "1px solid #d9d9d9", borderRadius: 4, marginTop: 8 }}
            />
          )}
        </div>
      )}

      {/* BTW bij Invoer */}
      <Row gutter={8}>
        <Col span={8}>
          <Form.Item label="BTW bij Invoer" style={{ marginBottom: 4 }}>
            {leanCustomsSource && btwValues.unitPrice > 0 ? (
              <Text style={{ fontSize: 12, color: "#52c41a", fontWeight: 600 }}>
                €{btwValues.unitPrice.toFixed(2)} <Tag color="green" style={{ fontSize: 9 }}>auto-filled</Tag>
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>Enter manually</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={16}>
          <Row gutter={8} justify="end">
            <Col span={4}>
              <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                <InputNumber min={0} value={btwValues.quantity} onChange={(v) => setBtwValues((p) => ({ ...p, quantity: v || 1 }))} style={{ width: "100%" }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0} step={0.01} value={btwValues.unitPrice}
                  onChange={(v) => setBtwValues((p) => ({ ...p, unitPrice: v || 0 }))}
                  onFocus={(e) => { if (btwValues.unitPrice === 0) e.target.select(); }}
                  style={{ width: "100%" }} size="small" placeholder="Enter amount"
                  formatter={(v) => v ? `€${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "€0"}
                  parser={(v) => { const p = v ? v.replace(/€\s?|(,*)/g, "") : "0"; return parseFloat(p) as unknown as 0; }}
                />
                {interestRate > 0 && (
                  <div style={{ fontSize: "10px", color: "#fa8c16", marginTop: "2px" }}>
                    +{interestRate}% over BTW+rechten (1 regel)
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                <InputNumber min={0} max={100} value={btwValues.vatPercentage} disabled style={{ width: "100%" }} size="small" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                <Button type="primary" size="small" style={{ width: "100%" }} onClick={addBtwToInvoiceLines} disabled={btwValues.unitPrice === 0}>{hasBtw ? "Update" : "Add"}</Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Invoerrechten */}
      <Row gutter={8} style={{ marginTop: 8 }}>
        <Col span={8}>
          <Form.Item label="Invoerrechten" style={{ marginBottom: 4 }}>
            {leanCustomsSource ? (
              <Text style={{ fontSize: 12, color: "#52c41a", fontWeight: 600 }}>
                €{invoerrechtenValues.unitPrice.toFixed(2)} <Tag color="green" style={{ fontSize: 9 }}>auto-filled</Tag>
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>Enter manually</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={16}>
          <Row gutter={8} justify="end">
            <Col span={4}>
              <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                <InputNumber min={0} value={invoerrechtenValues.quantity} onChange={(v) => setInvoerrechtenValues((p) => ({ ...p, quantity: v || 1 }))} style={{ width: "100%" }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0} step={0.01} value={invoerrechtenValues.unitPrice}
                  onChange={(v) => setInvoerrechtenValues((p) => ({ ...p, unitPrice: v || 0 }))}
                  onFocus={(e) => { if (invoerrechtenValues.unitPrice === 0) e.target.select(); }}
                  style={{ width: "100%" }} size="small" placeholder="Enter amount"
                  formatter={(v) => v ? `€${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "€0"}
                  parser={(v) => { const p = v ? v.replace(/€\s?|(,*)/g, "") : "0"; return parseFloat(p) as unknown as 0; }}
                />
                {interestRate > 0 && (btwAmount + dutiesAmount) > 0 && (
                  <div style={{ fontSize: "10px", color: "#fa8c16", marginTop: "2px" }}>
                    Provisie: €{((btwAmount + dutiesAmount) * (interestRate / 100)).toFixed(2)}
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                <InputNumber min={0} max={100} value={invoerrechtenValues.vatPercentage} disabled style={{ width: "100%" }} size="small" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                <Button type="primary" size="small" style={{ width: "100%" }} onClick={addInvoerrechtenToInvoiceLines}>{hasDuties ? "Update" : "Add"}</Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Add All button */}
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <Button type="primary" size="small" onClick={addAllToInvoiceLines} style={{ minWidth: 120 }}>
          {hasBtw || hasDuties ? "Update" : "Add"} All Customs Lines
        </Button>
      </div>
    </Card>
  );
};

export default CustomsSection;
